import React, { FC, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ItemDetails from '../ItemDetails';
import { GetFilteredAssetLoadManagements_getFilteredAssetLoadManagements as AssetLoad, } from '../../../types/schemaTypes';
import './AssetLoadItem.scss';
import { LoadStatusEnum } from '../../../types/LoadStatusEnum';
import { Routes } from '../../../utils';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { formatDateUtcToLocal } from '../../../utils/helpers/datetimeHelper';
import { CustomButton } from '../../_common';
import { OverlayTrigger } from 'react-bootstrap';

interface LoadItemProps {
    load: AssetLoad;
    isChecked: boolean;
    onChange: (checkedStatus: boolean) => void;
    onClickAssign: (loadId: number) => void;
}

const AssetLoadItem: FC<LoadItemProps> = ({
    load,
    isChecked,
    onChange,
    onClickAssign,
}: LoadItemProps): React.ReactElement => {
    const isAssignLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.AssignAssetLoads);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const {
        loadId,
        driverName,
        origin,
        destination,
        trailerType,
        carrierName,
        assetLoadType,
        status,
        atOriginIn,
        atOriginOut,
        atDestinationIn,
        atDestinationOut,
        equipments,
    } = load;

    const openLoadProfile = (loadId: number): void => {
        const URL = `${window.location.origin}${Routes.EQUIPMENT_MANAGEMENT.route}`;
        const newTab = window.open(URL + loadId, '_blank');
    };

    const checkForStatus = (status: string | null): boolean => {
        if (status === LoadStatusEnum.CREATED || status === 'ReadyForDispatch') {
            return true;
        }
        return false;
    };

    const loadTypeIndicator = {
        "Mobilization Load": "#7B8774",
        "Demobilization Load": "#72a0c1",
        "Equipment Transfer": "#B87333",
        "Equipment Move": "#F28F1C",
    };

    return (
        <div>
            {/* For Laptop */}
            <div className={'AssetLoadItem-Container ' + (isChecked ? 'AssetLoadItem-Selected' : '')}>
                <Col md="auto" className='checkbox-column'>
                    <div className="mb-3">
                        {checkForStatus(status) ? (
                            <input
                                type="checkbox"
                                className="AssetLoadItem-Checkbox"
                                checked={isChecked}
                                onChange={(event): void => {
                                    // abortRequest();
                                    onChange(event.currentTarget.checked);
                                }}
                            />
                        ) : (
                            <input
                                type="checkbox"
                                className="AssetLoadItem-Checkbox-Disabled"
                                checked={isChecked}
                                title="Cannot delete load"
                                disabled={true}
                                onChange={(event): void => {
                                    onChange(event.currentTarget.checked);
                                }}
                            />
                        )}
                    </div>
                </Col>

                <Col md={3} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                    <Row>
                        <OverlayTrigger
                            placement="top-start"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <span className="AssetLoadsManagement-LoadType-Indicator-Overlay">
                                    {assetLoadType}
                                </span>
                            }
                        >
                            <div className="AssetLoadsManagement-LoadType-Indicator" style={{ backgroundColor: loadTypeIndicator[assetLoadType ?? ''] }}></div>
                        </OverlayTrigger>
                        <span className="AssetLoadItem-Name" onClick={(): void => openLoadProfile(loadId ?? 0)}>
                            {`Load #: ${loadId}`}
                        </span>
                    </Row>
                    <ItemDetails label="Carrier:" info={carrierName || ''} />
                    <ItemDetails label="Driver:" info={driverName} />
                </Col>

                <Col md={4} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                    <ItemDetails label="Origin:" info={origin} />
                    <ItemDetails label="Destination:" info={destination} />
                    <ItemDetails label="Trailer Type:" info={trailerType} />
                </Col>

                <Col md={3} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                    <ItemDetails label="Status:" info={status} />
                    <ItemDetails label="Delivered Date:" info={formatDateUtcToLocal(atDestinationOut)} />
                    <ItemDetails
                        label="Equipment:"
                        info={equipments}
                    />
                </Col>

                <Col md={1} className="d-flex flex-column justify-content-between my-flex-container custom-width AssetLoadItem-Assign">
                    {status === "ReadyForDispatch" && isAssignLoadEnabled && (
                        <Row className="custom-driver-button">
                            <CustomButton isBordered title='Assign Driver' onClick={() => { onClickAssign(loadId ?? 0) }} />
                        </Row>
                    )}
                    <Row
                        className={`AssetLoadItem-LinkContainer AssetLoadItem-LinkContainer-More3`}
                        onClick={(): void => setIsDetailsOpen(!isDetailsOpen)}
                    >
                        <p className="AssetLoadItem-LinkText">{isDetailsOpen ? 'Less' : 'More'}</p>
                        <i className="material-icons AssetAssetLoadItem-Icon">
                            {isDetailsOpen ? 'expand_less' : 'expand_more'}
                        </i>
                    </Row>
                </Col>
            </div>

            {/* For Tab */}
            <div className={'AssetLoadItem-Container-For-Tab ' + (isChecked ? 'AssetLoadItem-Selected' : '')}>
                <Col md="auto" className='checkbox-column'>
                    <div className="mb-3">
                        {checkForStatus(status) ? (
                            <input
                                type="checkbox"
                                className="AssetLoadItem-Checkbox"
                                checked={isChecked}
                                onChange={(event): void => {
                                    // abortRequest();
                                    onChange(event.currentTarget.checked);
                                }}
                            />
                        ) : (
                            <input
                                type="checkbox"
                                className="AssetLoadItem-Checkbox"
                                checked={isChecked}
                                title="Cannot delete load"
                                disabled={true}
                                onChange={(event): void => {
                                    onChange(event.currentTarget.checked);
                                }}
                            />
                        )}
                    </div>
                </Col>

                <Col md={5} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                    <Row>
                        <OverlayTrigger
                            placement="top-start"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <span className="AssetLoadsManagement-LoadType-Indicator-Overlay">
                                    {assetLoadType}
                                </span>
                            }
                        >
                            <div className="AssetLoadsManagement-LoadType-Indicator" style={{ backgroundColor: loadTypeIndicator[assetLoadType ?? ''] }}></div>
                        </OverlayTrigger>
                        <span className="AssetLoadItem-Name" onClick={(): void => openLoadProfile(loadId ?? 0)}>
                            {`Load #: ${loadId}`}
                        </span>
                    </Row>
                    <ItemDetails label="Status:" info={status} />
                    <ItemDetails label="Carrier:" info={carrierName || ''} />
                    <ItemDetails label="Driver:" info={driverName} />
                    <ItemDetails label="Delivered Date:" info={formatDateUtcToLocal(atDestinationOut)} />
                </Col>

                <Col md={5} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                    <ItemDetails label="Origin:" info={origin} />
                    <ItemDetails label="Destination:" info={destination} />
                    <ItemDetails label="Trailer Type:" info={trailerType} />
                    <ItemDetails label="Equipment:" info={equipments} />
                </Col>

                {/* <Col md={4} className="d-flex flex-column justify-content-between my-flex-container custom-width">
                </Col> */}
            </div>

            {isDetailsOpen && (
                <div className="AssetLoadItem-DetailsContainer">
                    <div className="AssetLoadItem-DetailsSection">
                        <ItemDetails label="Origin In:" info={formatDateUtcToLocal(atOriginIn)} />
                    </div>
                    <div className="AssetLoadItem-Divider" />
                    <div className="AssetLoadItem-DetailsSection">
                        <ItemDetails label="Origin Out:" info={formatDateUtcToLocal(atOriginOut)} />
                    </div>
                    <div className="AssetLoadItem-Divider" />
                    <div className="AssetLoadItem-DetailsSection">
                        <ItemDetails label="Destination In:" info={formatDateUtcToLocal(atDestinationIn)} />
                    </div>
                    <div className="AssetLoadItem-Divider" />
                    <div className="AssetLoadItem-DetailsSection">
                        <ItemDetails label="Destination Out:" info={formatDateUtcToLocal(atDestinationOut)} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssetLoadItem;

